import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  styled,
  type SxProps,
  type Theme,
} from "@mui/material";
import React, { useState } from "react";
import { colors } from "../../ui/colors";

interface ActionDropdownItem {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

interface ActionDropdownButtonProps {
  actionLabel: string;
  actions?: ActionDropdownItem[];
  maxWidth?: string | number;
  fullWidth?: boolean;
  buttonSx?: SxProps<Theme>;
}

const ORANGE_BASE = colors.orange.base;

const PrimaryButton = styled(Button)({
  height: 36,
  minHeight: 36,
  backgroundColor: colors.orange.base,
  color: "#FFFFFF",
  padding: "0 16px",
  borderRadius: "999px",
  fontWeight: 800,
  fontSize: "0.8125rem",
  textTransform: "none",
  width: "100%",
  minWidth: 150,
  border: "1px solid transparent",
  boxShadow: "0 8px 18px rgba(234, 88, 12, 0.18)",
  transition: "background-color 140ms ease, box-shadow 140ms ease, transform 140ms ease",

  "&:hover": {
    backgroundColor: colors.orange[600] || colors.orange.base,
    boxShadow: "0 10px 24px rgba(234, 88, 12, 0.24)",
    border: "none",
  },

  "&:active": {
    transform: "translateY(1px)",
    boxShadow: "0 4px 12px rgba(234, 88, 12, 0.16)",
  },

  "& .MuiButton-endIcon": {
    marginLeft: 6,
    marginRight: -4,
  },

  "& .MuiButton-startIcon": {
    marginRight: 6,
  },

  "& .MuiSvgIcon-root": {
    fontSize: 18,
    color: "#FFFFFF",
  },

  "& .MuiButton-label": {
    whiteSpace: "nowrap",
  },
});

export const ActionDropdownButton: React.FC<ActionDropdownButtonProps> = ({
  actionLabel,
  actions = [],
  maxWidth = 240,
  fullWidth = true,
  buttonSx,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const safeActions = Array.isArray(actions) ? actions : [];
  const hasActions = safeActions.length > 0;

  return (
    <Box
      sx={{
        width: fullWidth ? "100%" : "auto",
        maxWidth,
        position: "relative",
      }}
    >
      <PrimaryButton
        variant="contained"
        disableElevation
        disabled={!hasActions}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              transform: isOpen ? "rotate(180deg)" : "none",
              transition: "0.2s",
            }}
          />
        }
        onClick={() => {
          if (!hasActions) return;
          setIsOpen((prev) => !prev);
        }}
        sx={{
          whiteSpace: "nowrap",
          ...buttonSx,
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            lineHeight: 1,
          }}
        >
          {actionLabel}
        </Box>
      </PrimaryButton>

      {hasActions && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            zIndex: 50,
          }}
        >
          <Collapse in={isOpen}>
            <Paper
              sx={{
                mt: 1,
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid var(--fx-surface-border, #E2E8F0)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              }}
            >
              <List disablePadding>
                {safeActions.map((action, index) => (
                  <React.Fragment key={action.label}>
                    <ListItemButton
                      onClick={() => {
                        action.onClick();
                        setIsOpen(false);
                      }}
                      sx={{
                        py: 1.2,
                        px: 2,
                        "&:hover": { bgcolor: `${ORANGE_BASE}05` },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: ORANGE_BASE }}>
                        {React.isValidElement(action.icon)
                          ? React.cloneElement(action.icon as React.ReactElement, {
                              sx: { fontSize: 18 },
                            })
                          : action.icon}
                      </ListItemIcon>

                      <ListItemText
                        primary={action.label}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          color: "var(--fx-surface-textsecondary, #64748B)",
                        }}
                      />
                    </ListItemButton>

                    {index < safeActions.length - 1 && <Divider sx={{ opacity: 0.5 }} />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
